import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Input,
  InputNumber,
  Pagination,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import type { TablePaginationConfig } from "antd/es/table";
import type { FilterValue, SorterResult } from "antd/es/table/interface";
import FileSaver from "file-saver";
import {
  downloadMpSyncDashboardCsv,
  getMpSyncDashboard,
  MpDashboardMarketplace,
  MpSyncDashboardItem,
  MpSyncDashboardQuery,
  MpSyncDashboardResponse,
  MpSyncDashboardSortField,
  MpSyncDiscrepancy,
} from "api/MpSyncDashboard";
import { patchRemnants, updateMPData, retireMp } from "../../api/ProductRemnants";
import { getBrands, getCategories } from "../../api/Goods";
import ArticleCell from "./ArticleCell";

const { Text } = Typography;

const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleString() : "—";

const STALE_1C_MINUTES = 60;
const STALE_MP_MINUTES = 10;

const minutesAgo = (iso?: string | null): number | null => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return (Date.now() - t) / 60000;
};

const formatMinutesAgo = (minutes: number) => {
  if (minutes < 1) return "менее 1 мин назад";
  if (minutes < 60) return `${Math.round(minutes)} мин назад`;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return m > 0 ? `${h}ч ${m}мин назад` : `${h}ч назад`;
};

const is1cFresh = (iso?: string | null) => {
  const age = minutesAgo(iso);
  return age != null && age <= STALE_1C_MINUTES;
};

const isMpFactFresh = (iso?: string | null) => {
  const age = minutesAgo(iso);
  return age != null && age <= STALE_MP_MINUTES;
};

const numOrUndefined = (v: number | null) =>
  typeof v === "number" && Number.isFinite(v) ? v : undefined;

const QUARANTINE_REASON_LABELS: Record<string, string> = {
  rate_limit: "Превышен лимит запросов",
  sku_absent: "Товар не найден на МП",
  error: "Ошибка синхронизации",
};

const displaySyncError = (row: MpSyncDashboardItem): string | null => {
  const text = row.sync_error?.trim();
  if (text) return text;
  if (isQuarantineActive(row.quarantine_at)) {
    const reason = row.quarantine_reason
      ? QUARANTINE_REASON_LABELS[row.quarantine_reason] ?? row.quarantine_reason
      : "Карантин";
    return reason;
  }
  if (row.discrepancy === "no_mp_fact") return "Факт МП не получен";
  if (!row.is_actual && row.actuality_reason?.trim()) {
    return row.actuality_reason.trim();
  }
  if (row.discrepancy !== "ok") {
    return DISCREPANCY_META[row.discrepancy].label;
  }
  return null;
};

const DISCREPANCY_META: Record<
  MpSyncDiscrepancy,
  { label: string; color: string }
> = {
  ok: { label: "OK", color: "green" },
  blocked: { label: "Заблокирован", color: "default" },
  quarantine: { label: "Карантин", color: "orange" },
  sync_error: { label: "Ошибка отправки", color: "red" },
  no_mp_fact: { label: "Нет факта МП", color: "gold" },
  mp_positive_1c_zero: { label: "МП>0, 1С=0", color: "volcano" },
  mp_zero_1c_positive: { label: "МП=0, 1С>0", color: "magenta" },
  mp_ne_target: { label: "МП≠цель", color: "blue" },
};

const isQuarantineActive = (iso?: string | null) =>
  Boolean(iso && new Date(iso).getTime() > Date.now());

// Компактные бейджи этапов флоу: 1С → Факт МП → Расчёт → Отправка → Карантин
const FlowSteps: React.FC<{ row: MpSyncDashboardItem }> = ({ row }) => {
  const quarantineActive = isQuarantineActive(row.quarantine_at);
  const r1Age = minutesAgo(row.remnant_1c_synced_at);
  const mpAge = minutesAgo(row.mp_stock_synced_at);
  const r1Fresh = is1cFresh(row.remnant_1c_synced_at);
  const mpFresh = isMpFactFresh(row.mp_stock_synced_at);
  const steps: { label: string; ok: boolean; title: string }[] = [
    {
      label: "1С",
      ok: r1Fresh,
      title: row.remnant_1c_synced_at
        ? r1Fresh
          ? `1С актуальна: ${formatDate(row.remnant_1c_synced_at)} (${formatMinutesAgo(r1Age!)})`
          : `1С устарела: ${formatDate(row.remnant_1c_synced_at)} (${formatMinutesAgo(r1Age!)}, порог ${STALE_1C_MINUTES} мин)`
        : "1С: нет данных об обновлении",
    },
    {
      label: "Факт",
      ok: mpFresh,
      title: row.mp_stock_synced_at
        ? mpFresh
          ? `Факт МП актуален: ${formatDate(row.mp_stock_synced_at)} (${formatMinutesAgo(mpAge!)})`
          : `Факт МП устарел: ${formatDate(row.mp_stock_synced_at)} (${formatMinutesAgo(mpAge!)}, порог ${STALE_MP_MINUTES} мин)`
        : "Факт МП: не получен",
    },
    {
      label: "Цель",
      ok: row.target_stock != null,
      title:
        row.target_stock != null
          ? `Целевой остаток: ${row.target_stock}${row.mp_reserved != null ? `, резерв: ${row.mp_reserved}` : ""}`
          : "Целевой остаток не рассчитан",
    },
    {
      label: "Отпр.",
      ok:
        !row.need_update &&
        row.last_sent_at != null &&
        !quarantineActive &&
        row.mp_stock === row.target_stock,
      title: [
        row.need_update ? "В очереди на отправку (need_update)" : null,
        row.mp_stock === row.target_stock
          ? `МП совпадает с целью. Отправка: ${formatDate(row.last_sent_at)}`
          : `МП (${row.mp_stock ?? "—"}) ≠ цель (${row.target_stock ?? "—"})`,
        row.last_sent_at
          ? `Последняя отправка: ${formatDate(row.last_sent_at)}, остаток ${row.last_sent_stock ?? "—"}`
          : "Отправка ещё не выполнялась",
      ]
        .filter(Boolean)
        .join(". "),
    },
    {
      label: "Каран.",
      ok: !quarantineActive,
      title: quarantineActive
        ? `В карантине до ${formatDate(row.quarantine_at)}: ${row.quarantine_reason ?? "—"}${row.sync_error ? ` · ${row.sync_error}` : ""}`
        : row.sync_error
          ? `Карантин снят, ошибка: ${row.sync_error}`
          : "Карантина нет",
    },
  ];
  return (
    <Space size={2} wrap>
      {steps.map((s) => {
        const inQuarantine = s.label === "Каран." && quarantineActive;
        return (
          <Tooltip key={s.label} title={s.title}>
            <Tag
              color={inQuarantine ? "orange" : s.ok ? "green" : "red"}
              style={{ margin: 0, fontSize: 11, padding: "0 4px" }}
            >
              {inQuarantine ? "⏸" : s.ok ? "✓" : "✕"}
              {s.label}
            </Tag>
          </Tooltip>
        );
      })}
    </Space>
  );
};

const EditableFormula: React.FC<{
  row: MpSyncDashboardItem;
  onSave: (row: MpSyncDashboardItem, value: string) => Promise<void>;
}> = ({ row, onSave }) => {
  const [value, setValue] = useState(row.share_formula ?? "");
  useEffect(() => setValue(row.share_formula ?? ""), [row.share_formula]);
  return (
    <Input
      size="small"
      value={value}
      style={{ width: 120 }}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => {
        if ((row.share_formula ?? "") !== value) onSave(row, value);
      }}
      onPressEnter={() => {
        if ((row.share_formula ?? "") !== value) onSave(row, value);
      }}
      placeholder="формула"
    />
  );
};

export const MpSyncDashboard: React.FC = () => {
  const [data, setData] = useState<MpSyncDashboardItem[]>([]);
  const [summary, setSummary] = useState<MpSyncDashboardResponse["summary"] | null>(
    null
  );
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [marketplace, setMarketplace] = useState<
    MpDashboardMarketplace | "all"
  >("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [discrepancyFilter, setDiscrepancyFilter] = useState<
    MpSyncDiscrepancy | undefined
  >(undefined);
  const [brandFilter, setBrandFilter] = useState<string | undefined>(undefined);
  const [categoryIdFilter, setCategoryIdFilter] = useState<number | undefined>(
    undefined
  );
  const [brands, setBrands] = useState<{ name: string }[]>([]);
  const [categories, setCategories] = useState<{ id: number; name: string }[]>(
    []
  );
  const [mpGt1c, setMpGt1c] = useState(false);
  const [needUpdate, setNeedUpdate] = useState(false);
  const [notActual, setNotActual] = useState(false);
  const [staleMinutes, setStaleMinutes] = useState<number | undefined>(undefined);
  const [actualitySummary, setActualitySummary] = useState<
    MpSyncDashboardResponse["actuality_summary"] | null
  >(null);
  const [needUpdateSummary, setNeedUpdateSummary] = useState<
    MpSyncDashboardResponse["need_update_summary"] | null
  >(null);
  const [sortField, setSortField] =
    useState<MpSyncDashboardSortField>("article");
  const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">("ASC");
  const [loading, setLoading] = useState(false);
  const [csvLoading, setCsvLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retirementEnabled] = useState(false);

  useEffect(() => {
    Promise.all([getBrands(), getCategories()])
      .then(([brandList, categoryList]) => {
        setBrands(brandList);
        setCategories(categoryList);
      })
      .catch(() => undefined);
  }, []);

  const query = useMemo<MpSyncDashboardQuery>(
    () => ({
      marketplace,
      search: search || undefined,
      page,
      limit,
      discrepancy: discrepancyFilter,
      brand: brandFilter,
      category_id: categoryIdFilter,
      mp_gt_1c: mpGt1c || undefined,
      need_update: needUpdate || undefined,
      not_actual: notActual || undefined,
      stale_mp_not_zero_minutes: staleMinutes,
      sort_field: sortField,
      sort_order: sortOrder,
    }),
    [
      marketplace,
      search,
      page,
      limit,
      discrepancyFilter,
      brandFilter,
      categoryIdFilter,
      mpGt1c,
      needUpdate,
      notActual,
      staleMinutes,
      sortField,
      sortOrder,
    ]
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getMpSyncDashboard(query);
      setData(response.data);
      setTotal(response.total);
      setSummary(response.summary);
      setActualitySummary(response.actuality_summary);
      setNeedUpdateSummary(response.need_update_summary);
    } catch (e) {
      setError("Не удалось загрузить дашборд синхронизации.");
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const mpDataKey = (mp: MpDashboardMarketplace) =>
    mp === "ozon" ? "ozon_data" : "wb_data";

  const saveFormula = useCallback(
    async (row: MpSyncDashboardItem, value: string) => {
      try {
        const field =
          row.marketplace === "ozon" ? "share_in_ozon" : "share_in_wildberries";
        await patchRemnants([
          { article: row.article, data: { [field]: value || null } as any },
        ]);
        message.success("Формула сохранена");
        loadData();
      } catch {
        message.error("Не удалось сохранить формулу");
      }
    },
    [loadData]
  );

  const toggleBlock = useCallback(
    async (row: MpSyncDashboardItem) => {
      try {
        await updateMPData([
          {
            article: row.article,
            [mpDataKey(row.marketplace)]: {
              disabled_for_send: !row.product_blocked,
            },
          } as any,
        ]);
        message.success(row.product_blocked ? "Разблокирован" : "Заблокирован");
        loadData();
      } catch {
        message.error("Не удалось изменить блокировку");
      }
    },
    [loadData]
  );

  const resetQuarantine = useCallback(
    async (row: MpSyncDashboardItem) => {
      try {
        await updateMPData([
          {
            article: row.article,
            [mpDataKey(row.marketplace)]: { quarantine_at: null },
          } as any,
        ]);
        message.success("Карантин снят");
        loadData();
      } catch {
        message.error("Не удалось снять карантин");
      }
    },
    [loadData]
  );

  const forceSend = useCallback(
    async (row: MpSyncDashboardItem) => {
      try {
        await updateMPData([
          {
            article: row.article,
            [mpDataKey(row.marketplace)]: { need_update_stock: true },
          } as any,
        ]);
        message.success("Помечен к отправке");
        loadData();
      } catch {
        message.error("Не удалось пометить к отправке");
      }
    },
    [loadData]
  );

  const toggleNeedUpdate = useCallback(
    async (row: MpSyncDashboardItem, next: boolean) => {
      if (next && row.product_blocked) {
        message.warning("Сначала снимите блокировку отправки");
        return;
      }
      if (next && isQuarantineActive(row.quarantine_at)) {
        message.warning("Товар в карантине — сначала снимите карантин");
        return;
      }
      try {
        await updateMPData([
          {
            article: row.article,
            [mpDataKey(row.marketplace)]: { need_update_stock: next },
          } as any,
        ]);
        message.success(next ? "Добавлен в очередь" : "Убран из очереди");
        loadData();
      } catch {
        message.error("Не удалось изменить очередь");
      }
    },
    [loadData]
  );

  const onRetire = useCallback(
    async (row: MpSyncDashboardItem, action: "retire" | "return") => {
      try {
        await retireMp([
          { article: row.article, marketplace: row.marketplace, action },
        ]);
        message.success(action === "retire" ? "Запущен вывод с МП" : "Возвращён");
        loadData();
      } catch {
        message.error("Не удалось выполнить действие");
      }
    },
    [loadData]
  );

  const downloadCsv = useCallback(async () => {
    setCsvLoading(true);
    try {
      const response = await downloadMpSyncDashboardCsv({
        ...query,
        page: 1,
        limit: 100000,
      });
      const blobPart = await response.blob();
      const blob = new Blob([blobPart], { type: "text/csv;charset=utf-8;" });
      FileSaver.saveAs(blob, "mp-sync-dashboard.csv");
    } finally {
      setCsvLoading(false);
    }
  }, [query]);

  const antSortOrder =
    sortOrder === "DESC" ? ("descend" as const) : ("ascend" as const);

  const columns: ColumnsType<MpSyncDashboardItem> = useMemo(
    () => [
      {
        title: "МП",
        dataIndex: "marketplace",
        key: "marketplace",
        width: 70,
        fixed: "left",
        render: (mp: MpDashboardMarketplace) => (
          <Tag color={mp === "ozon" ? "blue" : "purple"}>
            {mp === "ozon" ? "Ozon" : "WB"}
          </Tag>
        ),
      },
      {
        title: "Артикул",
        dataIndex: "article",
        key: "article",
        width: 200,
        fixed: "left",
        sorter: true,
        sortOrder: sortField === "article" ? antSortOrder : undefined,
        render: (article: string, row) => (
          <div style={{ fontSize: 12 }}>
            <ArticleCell article={article} />
            <div style={{ color: "#888" }}>{row.name}</div>
            {row.identifier && (
              <div style={{ color: "#aaa", fontSize: 11 }}>{row.identifier}</div>
            )}
          </div>
        ),
      },
      {
        title: "Актуальность",
        key: "is_actual",
        width: 200,
        render: (_: unknown, row) => (
          <Tooltip title={row.actuality_reason}>
            <Tag color={row.is_actual ? "green" : "red"}>
              {row.is_actual ? "Актуально" : "Не актуально"}
            </Tag>
            {!row.is_actual && (
              <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
                {row.actuality_reason.length > 80
                  ? `${row.actuality_reason.slice(0, 80)}…`
                  : row.actuality_reason}
              </div>
            )}
          </Tooltip>
        ),
      },
      {
        title: "Расхождение",
        dataIndex: "discrepancy",
        key: "discrepancy",
        width: 130,
        render: (d: MpSyncDiscrepancy) => (
          <Tag color={DISCREPANCY_META[d].color}>{DISCREPANCY_META[d].label}</Tag>
        ),
      },
      {
        title: "Этапы флоу",
        key: "flow",
        width: 210,
        render: (_: unknown, row) => <FlowSteps row={row} />,
      },
      {
        title: "Остаток 1С",
        dataIndex: "remnant_1c",
        key: "remnant_1c",
        width: 100,
        sorter: true,
        sortOrder: sortField === "remnant_1c" ? antSortOrder : undefined,
        render: (v: number | null, row) => {
          const age = minutesAgo(row.remnant_1c_synced_at);
          const fresh = is1cFresh(row.remnant_1c_synced_at);
          return (
            <Tooltip
              title={
                row.remnant_1c_synced_at
                  ? `Синхр. 1С: ${formatDate(row.remnant_1c_synced_at)}${age != null ? ` (${formatMinutesAgo(age)})` : ""}`
                  : "Нет данных об обновлении из 1С"
              }
            >
              <span style={{ color: fresh ? undefined : "#cf1322" }}>{v ?? "—"}</span>
            </Tooltip>
          );
        },
      },
      {
        title: "Факт МП",
        dataIndex: "mp_stock",
        key: "mp_stock",
        width: 110,
        sorter: true,
        sortOrder: sortField === "mp_stock" ? antSortOrder : undefined,
        render: (v: number | null, row) => {
          const age = minutesAgo(row.mp_stock_synced_at);
          const fresh = isMpFactFresh(row.mp_stock_synced_at);
          return (
            <Tooltip
              title={
                <>
                  {row.mp_stock_synced_at
                    ? `Подтянут: ${formatDate(row.mp_stock_synced_at)}${age != null ? ` (${formatMinutesAgo(age)})` : ""}`
                    : "Факт МП не получен"}
                  {row.mp_reserved != null && <div>Резерв: {row.mp_reserved}</div>}
                </>
              }
            >
              <span style={{ color: fresh ? undefined : "#cf1322" }}>{v ?? "—"}</span>
            </Tooltip>
          );
        },
      },
      {
        title: "Цель",
        dataIndex: "target_stock",
        key: "target_stock",
        width: 80,
        sorter: true,
        sortOrder: sortField === "target_stock" ? antSortOrder : undefined,
        render: (v: number | null) => v ?? "—",
      },
      {
        title: "Отправлено",
        dataIndex: "last_sent_stock",
        key: "last_sent_stock",
        width: 110,
        sorter: true,
        sortOrder: sortField === "last_sent_stock" ? antSortOrder : undefined,
        render: (v: number | null, row) => (
          <Tooltip title={`Дата отправки: ${formatDate(row.last_sent_at)}`}>
            <span>{v ?? "—"}</span>
          </Tooltip>
        ),
      },
      {
        title: "Формула",
        key: "share_formula",
        width: 140,
        render: (_: unknown, row) => (
          <EditableFormula row={row} onSave={saveFormula} />
        ),
      },
      {
        title: "Очередь",
        key: "need_update",
        width: 130,
        render: (_: unknown, row) => (
          <Tooltip
            title={
              row.product_blocked
                ? "Заблокировано — можно только снять очередь"
                : row.need_update
                  ? "Товар будет отправлен воркером sendRemnants"
                  : "Не в очереди на отправку"
            }
          >
            <div style={{ fontSize: 11 }}>
              <Checkbox
                checked={row.need_update}
                disabled={row.product_blocked && row.need_update}
                onChange={(e) => toggleNeedUpdate(row, e.target.checked)}
              >
                need_update
              </Checkbox>
              {row.need_update && (
                <Tag color="processing" style={{ marginTop: 4 }}>
                  Ждёт отправки
                </Tag>
              )}
            </div>
          </Tooltip>
        ),
      },
      {
        title: "Ошибка",
        dataIndex: "sync_error",
        key: "sync_error",
        width: 220,
        render: (_: string | null, row) => {
          const e = displaySyncError(row);
          if (!e) return "—";
          const fromFallback = !row.sync_error?.trim();
          return (
            <Tooltip title={e}>
              <Text
                type={fromFallback ? "warning" : "danger"}
                style={{ fontSize: 11 }}
              >
                {e.slice(0, 60)}
                {e.length > 60 ? "…" : ""}
              </Text>
            </Tooltip>
          );
        },
      },
      {
        title: "Действия",
        key: "actions",
        width: 260,
        fixed: "right",
        render: (_: unknown, row) => (
          <Space size={4} wrap>
            <Button size="small" onClick={() => toggleBlock(row)}>
              {row.product_blocked ? "Разблок." : "Блок"}
            </Button>
            {isQuarantineActive(row.quarantine_at) && (
              <Button size="small" onClick={() => resetQuarantine(row)}>
                Снять карантин
              </Button>
            )}
            {!row.product_blocked && (
              <Button size="small" onClick={() => forceSend(row)}>
                Отправить
              </Button>
            )}
            {retirementEnabled &&
              (row.retirement_state ? (
                <Button size="small" onClick={() => onRetire(row, "return")}>
                  Вернуть
                </Button>
              ) : (
                <Button size="small" danger onClick={() => onRetire(row, "retire")}>
                  Вывести
                </Button>
              ))}
          </Space>
        ),
      },
    ],
    [
      antSortOrder,
      sortField,
      saveFormula,
      toggleBlock,
      resetQuarantine,
      forceSend,
      toggleNeedUpdate,
      onRetire,
      retirementEnabled,
    ]
  );

  const handleTableChange = useCallback(
    (
      _pagination: TablePaginationConfig,
      _filters: Record<string, FilterValue | null>,
      sorter:
        | SorterResult<MpSyncDashboardItem>
        | SorterResult<MpSyncDashboardItem>[]
    ) => {
      const s = Array.isArray(sorter) ? sorter[0] : sorter;
      if (s && "field" in s && s.field != null) {
        const field = Array.isArray(s.field) ? s.field[0] : s.field;
        if (s.order) {
          setSortField(field as MpSyncDashboardSortField);
          setSortOrder(s.order === "descend" ? "DESC" : "ASC");
        } else {
          setSortField("article");
          setSortOrder("ASC");
        }
        setPage(1);
      }
    },
    []
  );

  const handlePaginationChange = useCallback((nextPage: number, nextLimit: number) => {
    setPage(nextPage);
    setLimit(nextLimit);
  }, []);

  const summaryFilter = (key: MpSyncDiscrepancy) => {
    if (!summary) return null;
    const active = discrepancyFilter === key;
    return (
      <Button
        key={key}
        type={active ? "primary" : "default"}
        onClick={() => {
          setPage(1);
          setDiscrepancyFilter(active ? undefined : key);
        }}
      >
        {DISCREPANCY_META[key].label}: <b>&nbsp;{summary[key]}</b>
      </Button>
    );
  };

  const discrepancyOptions = (
    Object.keys(DISCREPANCY_META) as MpSyncDiscrepancy[]
  ).map((key) => ({
    value: key,
    label: DISCREPANCY_META[key].label,
  }));

  return (
    <Card
      title="Дашборд синхронизации остатков (WB + Ozon)"
      style={{ marginTop: 16 }}
      extra={
        <Space>
          <Button onClick={loadData} loading={loading}>
            Обновить
          </Button>
          <Button onClick={downloadCsv} loading={csvLoading}>
            Выгрузить CSV
          </Button>
        </Space>
      }
    >
      {error && (
        <Alert type="error" message={error} style={{ marginBottom: 12 }} showIcon />
      )}

      <Space wrap style={{ marginBottom: 12 }}>
        <Segmented
          options={[
            { label: "Все МП", value: "all" },
            { label: "Wildberries", value: "wildberries" },
            { label: "Ozon", value: "ozon" },
          ]}
          value={marketplace}
          onChange={(v) => {
            setPage(1);
            setMarketplace(v as MpDashboardMarketplace | "all");
          }}
        />
        <Input
          placeholder="Поиск: артикул / название / идентификатор"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onPressEnter={() => {
            setPage(1);
            setSearch(searchInput.trim());
          }}
          style={{ width: 300 }}
        />
        <Button
          onClick={() => {
            setPage(1);
            setSearch(searchInput.trim());
          }}
        >
          Найти
        </Button>
        <Select
          allowClear
          placeholder="Расхождение"
          style={{ minWidth: 180 }}
          value={discrepancyFilter}
          options={discrepancyOptions}
          onChange={(value) => {
            setPage(1);
            setDiscrepancyFilter(value);
          }}
        />
        <Select
          allowClear
          showSearch
          placeholder="Бренд"
          style={{ minWidth: 160 }}
          value={brandFilter}
          optionFilterProp="label"
          options={brands.map((b) => ({ value: b.name, label: b.name }))}
          onChange={(value) => {
            setPage(1);
            setBrandFilter(value);
          }}
        />
        <Select
          allowClear
          showSearch
          placeholder="Категория"
          style={{ minWidth: 200 }}
          value={categoryIdFilter}
          optionFilterProp="label"
          options={categories.map((c) => ({ value: c.id, label: c.name }))}
          onChange={(value) => {
            setPage(1);
            setCategoryIdFilter(value);
          }}
        />
      </Space>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Критерии актуальности"
        description={
          <>
            1С — актуальна, если обновление было не позже {STALE_1C_MINUTES} мин назад.
            Факт МП — актуален, если подтянут не позже {STALE_MP_MINUTES} мин назад.
            При свежих данных, если МП ≠ целевому остатку — не актуально; причина
            указывается (ожидает отправки, карантин, блокировка и т.д.).
          </>
        }
      />

      <div style={{ marginBottom: 12 }}>
        <Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
          Актуальность (клик — фильтр):
        </Text>
        <Space wrap style={{ marginBottom: 8 }}>
          <Button
            type={notActual ? "primary" : "default"}
            danger={notActual}
            onClick={() => {
              setPage(1);
              setNotActual((v) => !v);
            }}
          >
            Не актуально: <b>&nbsp;{actualitySummary?.not_actual ?? "—"}</b>
          </Button>
          <Button
            type="default"
            onClick={() => {
              setPage(1);
              setNotActual(false);
            }}
            disabled={!notActual}
          >
            Актуально: {actualitySummary?.actual ?? "—"}
          </Button>
        </Space>
        <Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
          Расхождения (клик — фильтр):
        </Text>
        <Space wrap>
          {summaryFilter("ok")}
          {summaryFilter("no_mp_fact")}
          {summaryFilter("mp_positive_1c_zero")}
          {summaryFilter("mp_zero_1c_positive")}
          {summaryFilter("mp_ne_target")}
          {summaryFilter("sync_error")}
          {summaryFilter("quarantine")}
          {summaryFilter("blocked")}
          <Button
            type={mpGt1c ? "primary" : "default"}
            onClick={() => {
              setPage(1);
              setMpGt1c((v) => !v);
            }}
          >
            МП &gt; 1С
          </Button>
          <Button
            type={needUpdate ? "primary" : "default"}
            onClick={() => {
              setPage(1);
              setNeedUpdate((v) => !v);
            }}
          >
            Ждут отправки: <b>&nbsp;{needUpdateSummary?.queued ?? "—"}</b>
          </Button>
          <Space>
            <Text type="secondary">Не слали в МП, мин:</Text>
            <InputNumber
              min={1}
              value={staleMinutes}
              onChange={(value) => {
                setPage(1);
                setStaleMinutes(typeof value === "number" ? value : undefined);
              }}
            />
          </Space>
          {discrepancyFilter && (
            <Button
              type="link"
              onClick={() => {
                setPage(1);
                setDiscrepancyFilter(undefined);
              }}
            >
              Сбросить фильтр расхождения
            </Button>
          )}
        </Space>
      </div>

      <Table<MpSyncDashboardItem>
        rowKey={(row) => `${row.marketplace}:${row.article}`}
        size="small"
        sticky
        scroll={{ x: 2040, y: "calc(100vh - 460px)" }}
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={false}
        onChange={handleTableChange}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginTop: 16,
          paddingTop: 8,
          position: "relative",
          zIndex: 10,
        }}
      >
        <Pagination
          current={page}
          pageSize={limit}
          total={total}
          showSizeChanger
          pageSizeOptions={[20, 50, 100, 200]}
          showTotal={(t, range) => `${range[0]}-${range[1]} из ${t}`}
          onChange={handlePaginationChange}
          onShowSizeChange={handlePaginationChange}
        />
      </div>
    </Card>
  );
};

export default MpSyncDashboard;
